import React, { useCallback, useEffect, useState } from 'react';
import { Flex,Text, View, ProgressCircle, TreeView, TreeViewItem, TreeViewItemContent } from '@adobe/react-spectrum';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { useViewInfo } from '../../../../../hooks/UseViewInfo';
import { useDependency } from '../../../../../contexts/DependencyProvider';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../../state/store';
import { useSelector } from 'react-redux';
import styles from './distribution_group_filter_component.module.css';
import { ListDistributionGroupRequest } from '../../../../../services/soap/main/requests/ListDistributionGroupRequest';

interface DistributionGroupFilterComponentProps {
  createParams: (filterType: any, filterValue: any) => void;
  filterParams?: any;
  clearFilters?:boolean
}

const DistributionGroupFilterComponent: React.FC<DistributionGroupFilterComponentProps> = ({ createParams, filterParams,clearFilters }) => {
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const{store,mainService} = useDependency();
  const { isMobile } = useViewInfo();
  const { t } = useTranslation();
  const _t = (key: string) => t(`contact_filters.${key}`, { ns: 'contacts' });
  const [toggleListVisibility, setToggleListVisibility] = useState<any>({ stateList: false });
  const [selectedGroupKey, setSelectedGroupKey] = useState<Set<string>>(new Set(['All groups']));
  const [distributionGroups,setDistributionGroups] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  const handleSelectionChange = (value: any) => {
    const arrValue = Array.from(value);
    const filteredValue = arrValue.filter(item => item !== 'All groups');
    const updateValue = filteredValue.length > 0 ? new Set(filteredValue) : new Set(['All groups']);
    setSelectedGroupKey(new Set(Array.from(updateValue).map(String)));
    createParams('groups', updateValue);
  };

  useEffect(() => {
    if(clearFilters){
      setSelectedGroupKey(new Set(['All groups']));
    }
  }, [clearFilters]);

   useEffect(() => {
      (async () => {
        setSelectedGroupKey(new Set(['All groups']));
        await getActivityStatusArray();
      })();
    }, [selectedApp]);

  const getActivityStatusArray = useCallback(async () => {
      try {
        let res = await mainService.listDistributionGroup(new ListDistributionGroupRequest(store.Server, store.SessionId));
        if (res.result === 'OK' && res.DGROUP.length > 0) {
          const groupedData = groupData(res.DGROUP)
          setDistributionGroups(groupedData);
        }
      } finally {
        setShowLoader(false);
      }
    }, []);

      const groupData = (data: any[]) => {
    const grouped: Record<string, { categoryName: string; groups: any[] }> = {};

    data.forEach((item) => {
        if (item.categoryId) {
            if (!grouped[item.categoryId]) {
                grouped[item.categoryId] = { categoryName: item.categoryName, groups: [] };
            }
            grouped[item.categoryId].groups.push(item);
        }
    });

    const groupedItems = Object.values(grouped).map((category) => ({
        key: category.categoryName,
        name: `${category.categoryName} (${category.groups.length})`,
        children: category.groups.map((group) => ({
            key: group.id,
            name: group.name
        }))
    }));

    const ungroupedItems = data.filter((item) => !item.categoryId).map((item) => ({
        key: item.id,
        name: item.name
    }));

    return [...groupedItems, ...ungroupedItems];
};

  if (showLoader) {
    return (
      <Flex width="100%" height="100%" UNSAFE_style={{ backgroundColor: 'white' }} justifyContent={'center'} alignContent={'center'} alignItems={'center'}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <Flex direction={'column'} minWidth={'200px'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'}>
        <Flex direction={'column'} gap={'4px'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.group_container}>
          <Flex direction={'row'} height={'36px'} alignSelf={'stretch'} alignItems={'center'} justifyContent={'space-between'} UNSAFE_className={styles.group_heading}>
            <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
              <View position={'relative'} UNSAFE_className={styles.group_heading_text}>
                {/* {t('group', { ns: 'layout_components' })} */}
                {_t('groups')}
              </View>
            </Flex>

            {toggleListVisibility.stateList ? (
              <ChevronDown color="#B0BEC5" onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, stateList: false }))} style={{ cursor: 'pointer' }} />
            ) : (
              <ChevronUp color="#B0BEC5" onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, stateList: true }))} style={{ cursor: 'pointer' }} />
            )}
          </Flex>
          <Flex
            direction={'column'}
            alignSelf={'stretch'}
            alignItems={'start'}
            justifyContent={'start'}
            UNSAFE_className={isMobile ? styles.group_component_mobile : styles.group_component}
            UNSAFE_style={{ display: toggleListVisibility.stateList ? '' : 'none' }}
          >
            <TreeView 
                    id="contactDGroups"
                    aria-label="Distribution Groups"
                    selectionMode="multiple" 
                    //selectedKeys={distributionGroups.map((group:any) => group.id ? group.id : '')}
                    onSelectionChange={handleSelectionChange}
                    >
                  {distributionGroups && distributionGroups.map((node:any) =>
                    node.children ? (
                      <TreeViewItem key={node.key} id={node.key} textValue={node.name}>
                        <TreeViewItemContent>
                          <Text>{node.name}</Text>
                        </TreeViewItemContent>
                        {node.children.map((child:any) => (
                          <TreeViewItem key={child.key} id={child.key} textValue={child.name}>
                            <TreeViewItemContent>
                              <Text>{child.name}</Text>
                            </TreeViewItemContent>
                          </TreeViewItem>
                        ))}
                      </TreeViewItem>
                    ) : (
                      <TreeViewItem key={node.key} id={node.key} textValue={node.name}>
                        <TreeViewItemContent>
                          <Text>{node.name}</Text>
                        </TreeViewItemContent>
                      </TreeViewItem>
                    )
                  )}
                </TreeView>
          </Flex>
        </Flex>
      </Flex>
    );
  }
};

export default DistributionGroupFilterComponent;
